/**
 * Dunea Remote on the web — manage the agent sessions running on your Macs
 * from the browser. Same E2EE relay protocol as the mobile app: this tab is
 * a client device (pair → SAS check → approve on the Mac).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { relay } from '~/lib/remote/relay';
import { allAttention, useRemote } from '~/lib/remote/store';
import type { AttentionEntry, BranchReview, ChatItem, PrDetail, PrInfo, SessionRow } from '~/lib/remote/protocol';

function timeAgo(ts: number): string {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));

  if (s < 60) {
    return `${s}s`;
  }

  if (s < 3600) {
    return `${Math.floor(s / 60)}m`;
  }

  if (s < 86400) {
    return `${Math.floor(s / 3600)}h`;
  }

  return `${Math.floor(s / 86400)}d`;
}

const STATUS_DOT: Record<string, string> = {
  working: 'bg-blue-400 animate-pulse',
  starting: 'bg-blue-400 animate-pulse',
  awaiting_permission: 'bg-amber-400',
  error: 'bg-red-400',
  exited: 'bg-red-400/60',
  idle: 'bg-white/25',
};

export default function RemotePage() {
  const remote = useRemote();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    void remote.init();
  }, []);

  // visibility → presence + reconnect nudge
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        relay.nudge();
        relay.presence(activeSessionId);
      } else {
        relay.presence(null);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => document.removeEventListener('visibilitychange', onVis);
  }, [activeSessionId]);

  const paired = remote.identity && remote.deviceState === 'approved';

  return (
    // Bounded height so the transcript scrolls internally and the composer stays on screen.
    <div className="ui-root h-[100dvh] bg-page flex flex-col">
      <header className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-hairline">
        <Link to="/" className="shrink-0 text-secondary hover:text-primary text-[13px] no-underline">
          ← Dunea
        </Link>
        <span className="ui-serif text-[18px] text-primary">Remote</span>
        <span
          className={`inline-block w-2 h-2 shrink-0 rounded-full ${remote.relayConnected ? 'bg-emerald-400' : 'bg-white/20'}`}
          title={remote.relayConnected ? 'Relay connected' : 'Relay offline'}
        />
        <span className="hidden sm:inline text-[12px] text-muted">
          {Object.values(remote.sidecars).filter((sc) => sc.handshaken).length} Mac(s) connected
        </span>
        <div className="flex-1" />
        {remote.identity ? (
          <button className="ui-btn-ghost shrink-0" onClick={() => void remote.unpair()}>
            <span className="sm:hidden">Unlink</span>
            <span className="hidden sm:inline">Unlink this browser</span>
          </button>
        ) : null}
      </header>

      {!paired ? (
        <PairingGate />
      ) : (
        // Below md this is master → detail: the session list, or one session with a back button.
        <div className="flex flex-1 min-h-0">
          <Sidebar activeSessionId={activeSessionId} onSelect={setActiveSessionId} />
          {activeSessionId ? (
            <SessionPane key={activeSessionId} sessionId={activeSessionId} onBack={() => setActiveSessionId(null)} />
          ) : (
            <div className="hidden md:flex flex-1 items-center justify-center">
              <p className="text-secondary text-[14px]">Pick a session — or approve something in “Needs you.”</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── pairing ──────────────────────────────────────────────────────────────────

function PairingGate() {
  const remote = useRemote();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // poll registry while waiting for the Mac's approval
  useEffect(() => {
    if (remote.identity && remote.deviceState !== 'approved') {
      const t = setInterval(() => void remote.refreshDevices().catch(() => undefined), 3000);
      return () => clearInterval(t);
    }

    return undefined;
  }, [remote.identity, remote.deviceState]);

  const pair = async () => {
    setBusy(true);
    setError(null);

    try {
      await remote.pair();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Pairing failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto flex items-center [align-items:safe_center] justify-center p-4 sm:p-8">
      <div className="ui-card w-full max-w-[480px] p-6 sm:p-8">
        <p className="ui-caption text-accent mb-2">Dunea Remote</p>
        <h1 className="ui-serif text-[28px] text-primary m-0 mb-3">Control your Macs from this browser</h1>
        <p className="text-[13.5px] text-secondary leading-[1.65] mb-6">
          This tab becomes a paired device — end-to-end encrypted to each Mac running Dunea Desktop. The relay only ever
          sees ciphertext.
        </p>

        {!remote.identity ? (
          <>
            {error ? <p className="text-[13px] text-danger mb-3">{error}</p> : null}
            <button className="ui-btn-accent w-full" onClick={() => void pair()} disabled={busy}>
              {busy ? 'Registering…' : 'Pair this browser'}
            </button>
          </>
        ) : remote.deviceState === 'denied' || remote.deviceState === 'revoked' ? (
          <>
            <p className="text-[13px] text-danger mb-4">
              This browser was {remote.deviceState}. Generate a fresh key to try again.
            </p>
            <button className="ui-btn-secondary w-full" onClick={() => void remote.unpair()}>
              Start over
            </button>
          </>
        ) : (
          <>
            <p className="text-[13.5px] text-secondary mb-4">
              On your Mac: <strong className="text-primary">Dunea Desktop → Settings → Connections</strong> — approve
              this browser if the words match:
            </p>
            <div className="bg-sunken rounded-lg py-4 text-center mb-4">
              <span className="text-accent text-[18px] font-semibold tracking-wide">{remote.fingerprint}</span>
            </div>
            <p className="text-[12px] text-muted">
              Different words on the Mac? Deny the request — someone else may be pairing.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ activeSessionId, onSelect }: { activeSessionId: string | null; onSelect: (id: string) => void }) {
  const remote = useRemote();
  const attention = allAttention(remote);
  const [composeFor, setComposeFor] = useState<{ sidecarId: string; projectId: string; name: string } | null>(null);

  return (
    <aside
      className={`w-full md:w-[320px] shrink-0 md:border-r border-hairline overflow-y-auto ${
        activeSessionId ? 'hidden md:block' : ''
      }`}
    >
      {attention.length > 0 ? (
        <div className="p-3 border-b border-hairline">
          <p className="ui-caption text-secondary px-1 mb-2">Needs you</p>
          <div className="flex flex-col gap-1.5">
            {attention.map((entry) => (
              <AttentionRow key={entryKey(entry)} entry={entry} onSelect={onSelect} />
            ))}
          </div>
        </div>
      ) : null}

      {Object.values(remote.sidecars).map((sc) =>
        sc.projects.map((project) => {
          const worktrees = sc.worktrees.filter((w) => w.projectId === project.id);
          const sessions = sc.sessions
            .filter((row) => worktrees.some((w) => w.id === row.worktreeId))
            .sort((a, b) => b.lastActiveAt - a.lastActiveAt);

          return (
            <div key={`${sc.deviceId}:${project.id}`} className="p-3">
              <div className="flex items-center px-1 mb-1.5">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-primary font-semibold truncate m-0">{project.name}</p>
                  <p className="text-[11px] text-muted m-0">{sc.name}</p>
                </div>
                <button
                  className="ui-btn-ghost !text-[11.5px]"
                  onClick={() => setComposeFor({ sidecarId: sc.deviceId, projectId: project.id, name: project.name })}
                >
                  + Task
                </button>
              </div>
              {sessions.map((row) => (
                <SessionRowItem
                  key={row.id}
                  row={row}
                  branch={worktrees.find((w) => w.id === row.worktreeId)?.branch}
                  active={row.id === activeSessionId}
                  onSelect={onSelect}
                />
              ))}
              {sessions.length === 0 ? <p className="text-[12px] text-muted px-1">No sessions.</p> : null}
            </div>
          );
        }),
      )}

      {composeFor ? <ComposeDialog target={composeFor} onClose={() => setComposeFor(null)} /> : null}
    </aside>
  );
}

function entryKey(entry: AttentionEntry & { sidecarId: string }): string {
  return entry.kind === 'session' ? `s:${entry.sessionId}` : `pr:${entry.worktreeId}`;
}

const REASON_META = {
  permission: { label: 'Needs approval', cls: 'text-amber-300 bg-amber-400/10' },
  error: { label: 'Errored', cls: 'text-red-300 bg-red-400/10' },
  done: { label: 'Finished', cls: 'text-emerald-300 bg-emerald-400/10' },
} as const;

function AttentionRow({
  entry,
  onSelect,
}: {
  entry: AttentionEntry & { sidecarId: string };
  onSelect: (id: string) => void;
}) {
  const remote = useRemote();
  const sc = remote.sidecars[entry.sidecarId];

  if (entry.kind === 'pr') {
    const wt = sc?.worktrees.find((w) => w.id === entry.worktreeId);
    const session = sc?.sessions
      .filter((row) => row.worktreeId === entry.worktreeId)
      .sort((a, b) => b.lastActiveAt - a.lastActiveAt)[0];

    return (
      <button
        className="text-left bg-raised hover:bg-white/[0.06] border border-hairline rounded-lg px-3 py-2 cursor-pointer"
        onClick={() => session && onSelect(session.id)}
      >
        <span className="text-[11px] text-accent bg-accent/10 rounded px-1.5 py-0.5">PR ready</span>
        <p className="text-[13px] text-primary truncate m-0 mt-1">{entry.title || wt?.branch || 'Pull request'}</p>
      </button>
    );
  }

  const session = sc?.sessions.find((row) => row.id === entry.sessionId);
  const meta = REASON_META[entry.reason];

  return (
    <button
      className="text-left bg-raised hover:bg-white/[0.06] border border-hairline rounded-lg px-3 py-2 cursor-pointer"
      onClick={() => onSelect(entry.sessionId)}
    >
      <span className={`text-[11px] rounded px-1.5 py-0.5 ${meta.cls}`}>{meta.label}</span>
      <p className="text-[13px] text-primary truncate m-0 mt-1">{session?.title || 'Session'}</p>
    </button>
  );
}

function SessionRowItem({
  row,
  branch,
  active,
  onSelect,
}: {
  row: SessionRow;
  branch?: string;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      className={`w-full text-left flex items-center gap-2.5 rounded-lg px-2.5 py-2 cursor-pointer border-0 ${
        active ? 'bg-sunken' : 'bg-transparent hover:bg-white/[0.04]'
      }`}
      onClick={() => onSelect(row.id)}
    >
      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[row.status] ?? STATUS_DOT.idle}`} />
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] text-primary truncate">{row.title || branch || 'Session'}</span>
        <span className="block text-[11px] text-muted truncate">{branch}</span>
      </span>
      <span className="text-[11px] text-muted shrink-0">{timeAgo(row.lastActiveAt)}</span>
    </button>
  );
}

// ── compose ──────────────────────────────────────────────────────────────────

function ComposeDialog({
  target,
  onClose,
}: {
  target: { sidecarId: string; projectId: string; name: string };
  onClose: () => void;
}) {
  const remote = useRemote();
  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setBusy(true);
    setError(null);

    try {
      await remote.createTask(target.sidecarId, target.projectId, task.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center [align-items:safe_center] justify-center overflow-y-auto p-3 sm:p-6"
      onClick={onClose}
    >
      <div className="ui-card max-w-[520px] w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="ui-serif text-[20px] text-primary m-0 mb-1">New task — {target.name}</h2>
        <p className="text-[12.5px] text-secondary mb-4">
          A worktree + agent session starts on your Mac. Remote tasks always use standard permission prompts.
        </p>
        <textarea
          className="ui-input min-h-[110px] resize-y mb-3"
          placeholder="What should the agent do?"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          autoFocus
        />
        {error ? <p className="text-[13px] text-danger mb-3">{error}</p> : null}
        <div className="flex gap-2 justify-end">
          <button className="ui-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="ui-btn-accent" onClick={() => void start()} disabled={!task.trim() || busy}>
            {busy ? 'Starting…' : 'Start task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── session pane ─────────────────────────────────────────────────────────────

const TABS = ['Conversation', 'Changes', 'Checks'] as const;

function SessionPane({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const remote = useRemote();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Conversation');
  const sidecarId = remote.sessionOwner[sessionId];
  const sc = sidecarId ? remote.sidecars[sidecarId] : undefined;
  const session = sc?.sessions.find((row) => row.id === sessionId);
  const worktree = sc?.worktrees.find((w) => w.id === session?.worktreeId);

  useEffect(() => {
    if (!sidecarId) {
      return undefined;
    }

    relay.subscribe(sidecarId, sessionId, true);
    relay.presence(sessionId);
    void remote.loadTranscript(sessionId).catch(() => undefined);
    remote.markRead(sessionId, Date.now());

    return () => {
      relay.subscribe(sidecarId, sessionId, false);
      relay.presence(null);
    };
  }, [sessionId, sidecarId]);

  return (
    <main className="flex-1 min-w-0 flex flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 md:px-5 py-2.5 border-b border-hairline">
        <button className="ui-btn-icon md:hidden -ml-1.5" onClick={onBack} aria-label="Back to sessions">
          <span className="i-ph:arrow-left text-base" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] text-primary font-semibold truncate m-0">{session?.title || 'Session'}</p>
          <p className="text-[11.5px] text-muted truncate m-0">
            {worktree?.branch} · {sc?.name}
          </p>
        </div>
        <div className="ui-segmented flex w-full sm:w-auto">
          {TABS.map((t) => (
            <button
              key={t}
              className={`ui-segmented-btn flex-1 sm:flex-none ${tab === t ? 'ui-segmented-btn-active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      {tab === 'Conversation' ? (
        <Conversation sessionId={sessionId} />
      ) : tab === 'Changes' ? (
        <ChangesPanel worktreeId={session?.worktreeId} sidecarId={sidecarId} />
      ) : (
        <ChecksPanel worktreeId={session?.worktreeId} sidecarId={sidecarId} />
      )}
    </main>
  );
}

// ── conversation ─────────────────────────────────────────────────────────────

function toolSummary(item: Extract<ChatItem, { kind: 'tool_use' }>): string {
  const input = item.input ?? {};
  const first =
    (input.file_path as string) ||
    (input.path as string) ||
    (input.command as string) ||
    (input.pattern as string) ||
    (input.url as string) ||
    (input.prompt as string) ||
    '';
  const trimmed = String(first).replace(/\s+/g, ' ').slice(0, 96);

  return trimmed ? `${item.name} · ${trimmed}` : item.name;
}

function TranscriptRow({ item }: { item: ChatItem }) {
  const [open, setOpen] = useState(false);

  switch (item.kind) {
    case 'user':
      return (
        <div className="flex justify-end my-2">
          <div className="bg-accent/12 border border-accent/20 rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[85%]">
            <p className="text-[13.5px] text-primary whitespace-pre-wrap [overflow-wrap:anywhere] m-0">{item.text}</p>
          </div>
        </div>
      );
    case 'assistant_text':
      return (
        <p className="text-[13.5px] text-primary leading-[1.65] whitespace-pre-wrap [overflow-wrap:anywhere] my-2">
          {item.text}
        </p>
      );
    case 'thinking':
      return <p className="text-[12px] text-muted italic leading-[1.5] my-1.5 line-clamp-4">{item.text}</p>;
    case 'tool_use': {
      const failed = item.result?.isError;
      const running = !item.result;

      return (
        <button
          className="w-full text-left flex gap-2 items-start bg-transparent border-0 px-0 py-0.5 cursor-pointer"
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`text-[11px] mt-0.5 ${failed ? 'text-red-400' : running ? 'text-blue-400' : 'text-muted'}`}>
            {failed ? '✕' : running ? '…' : '✓'}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-geist-mono text-[11.5px] text-secondary truncate">{toolSummary(item)}</span>
            {open && item.result ? (
              <pre className="font-geist-mono text-[11px] text-muted bg-sunken rounded-md p-2 mt-1.5 max-h-[240px] overflow-auto whitespace-pre-wrap">
                {item.result.text.slice(0, 4000) || '(no output)'}
              </pre>
            ) : null}
          </span>
        </button>
      );
    }
    case 'turn_end':
      return (
        <div className="flex items-center gap-3 my-3">
          <span className="flex-1 h-px bg-white/8" />
          <span className="text-[11px] text-muted">
            {item.isError ? 'errored' : 'done'}
            {item.durationMs ? ` · ${Math.round(item.durationMs / 1000)}s` : ''}
            {item.costUsd ? ` · $${item.costUsd.toFixed(2)}` : ''}
          </span>
          <span className="flex-1 h-px bg-white/8" />
        </div>
      );
    case 'compact_boundary':
      return <p className="text-[11px] text-muted text-center my-2">— context compacted —</p>;
    case 'error':
      return (
        <div className="bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2 my-2">
          <p className="text-[12.5px] text-red-300 m-0">{item.text}</p>
        </div>
      );
    default:
      return null;
  }
}

function Conversation({ sessionId }: { sessionId: string }) {
  const remote = useRemote();
  const transcript = remote.transcripts[sessionId];
  const status = transcript?.status ?? 'idle';
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const items = useMemo(
    () => (transcript?.items ?? []).filter((it) => !('parentToolUseId' in it && it.parentToolUseId)),
    [transcript?.items],
  );

  useEffect(() => {
    const el = scrollRef.current;

    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [items.length, transcript?.streamText, transcript?.streamThinking]);

  const send = useCallback(async () => {
    const text = draft.trim();

    if (!text) {
      return;
    }

    setSending(true);

    try {
      await remote.sendMessage(sessionId, text);
      setDraft('');
    } finally {
      setSending(false);
    }
  }, [draft, sessionId]);

  const working = status === 'working' || status === 'starting';

  return (
    <>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
        {!transcript?.loaded ? <p className="text-[13px] text-muted">Loading transcript…</p> : null}
        {items.map((item) => (
          <TranscriptRow key={item.id} item={item} />
        ))}
        {transcript?.streamThinking ? (
          <p className="text-[12px] text-muted italic my-1.5 line-clamp-3">{transcript.streamThinking}</p>
        ) : null}
        {transcript?.streamText ? (
          <p className="text-[13.5px] text-primary leading-[1.65] whitespace-pre-wrap my-2">{transcript.streamText}</p>
        ) : null}
      </div>

      {(transcript?.pendingPermissions ?? []).map((p) => (
        <div key={p.requestId} className="mx-4 md:mx-6 mb-3 bg-amber-400/8 border border-amber-400/30 rounded-xl p-4">
          <p className="text-[11px] text-amber-300 uppercase tracking-wide m-0 mb-1">Permission request</p>
          <p className="text-[13.5px] text-primary font-semibold m-0">{p.toolName}</p>
          <pre className="font-geist-mono text-[11.5px] text-secondary bg-sunken rounded-md p-2 mt-2 max-h-[140px] overflow-auto whitespace-pre-wrap">
            {typeof p.input.command === 'string'
              ? p.input.command
              : typeof p.input.file_path === 'string'
                ? p.input.file_path
                : JSON.stringify(p.input, null, 1).slice(0, 600)}
          </pre>
          <div className="flex gap-2 mt-3 justify-end">
            <button
              className="ui-btn-secondary !text-[12.5px]"
              onClick={() => void remote.respondPermission(sessionId, p.requestId, false).catch(() => undefined)}
            >
              Deny
            </button>
            <button
              className="ui-btn-accent !text-[12.5px]"
              onClick={() => void remote.respondPermission(sessionId, p.requestId, true).catch(() => undefined)}
            >
              Allow
            </button>
          </div>
        </div>
      ))}

      <div className="border-t border-hairline px-4 md:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[status] ?? STATUS_DOT.idle}`} />
          <span className="text-[12px] text-secondary">
            {working ? 'Working…' : status === 'awaiting_permission' ? 'Waiting for approval' : status}
          </span>
          {(transcript?.queue?.length ?? 0) > 0 ? (
            <span className="text-[11.5px] text-muted">· {transcript!.queue.length} queued</span>
          ) : null}
          <span className="flex-1" />
          {working ? (
            <button
              className="ui-btn-ghost !text-[12px] text-red-300"
              onClick={() => void remote.interrupt(sessionId).catch(() => undefined)}
            >
              Stop
            </button>
          ) : null}
        </div>
        <div className="flex gap-2 items-end">
          <textarea
            className="ui-input min-h-[42px] max-h-[160px] resize-y flex-1"
            placeholder={working ? 'Queue a follow-up…' : 'Message the agent…'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button className="ui-btn-accent" onClick={() => void send()} disabled={!draft.trim() || sending}>
            Send
          </button>
        </div>
      </div>
    </>
  );
}

// ── changes / checks ─────────────────────────────────────────────────────────

const FILE_STATUS: Record<string, string> = {
  M: 'text-amber-300',
  A: 'text-emerald-300',
  D: 'text-red-300',
  R: 'text-blue-300',
  C: 'text-blue-300',
};

function ChangesPanel({ worktreeId, sidecarId }: { worktreeId?: string; sidecarId?: string }) {
  const remote = useRemote();
  const [review, setReview] = useState<BranchReview | null>(null);
  const [pr, setPr] = useState<PrInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  const load = useCallback(async () => {
    if (!worktreeId || !sidecarId) {
      return;
    }

    setError(null);

    try {
      const [r, p] = await Promise.all([
        remote.rpcFor<BranchReview>(sidecarId, 'worktree.branchReview', { worktreeId }),
        remote.rpcFor<PrInfo>(sidecarId, 'pr.info', { worktreeId }).catch(() => null),
      ]);
      setReview(r);
      setPr(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load changes');
    }
  }, [worktreeId, sidecarId]);

  useEffect(() => {
    void load();
  }, [load]);

  const mergeReady =
    pr?.exists &&
    pr.state === 'OPEN' &&
    pr.checks !== 'failing' &&
    (pr.unresolvedThreads ?? 0) === 0 &&
    pr.mergeable !== 'CONFLICTING';

  const merge = async () => {
    if (!worktreeId || !window.confirm('Merge this PR now? Merging is always your call.')) {
      return;
    }

    setMerging(true);

    try {
      const res = await remote.merge(worktreeId);

      if (res.status !== 'merged') {
        setError(res.message ?? `Not merged: ${res.status}`);
      } else {
        void load();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4 flex flex-col gap-4">
      {error ? <p className="text-[13px] text-danger">{error}</p> : null}

      <section className="ui-card p-4">
        {pr?.exists ? (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[11.5px] text-emerald-300 bg-emerald-400/10 rounded px-1.5 py-0.5">
                PR #{pr.number} · {pr.state}
              </span>
              {pr.url ? (
                <a href={pr.url} target="_blank" rel="noreferrer" className="text-[12px] text-blue-300 no-underline">
                  Open on GitHub ↗
                </a>
              ) : null}
            </div>
            <p className="text-[14px] text-primary font-semibold m-0 mb-1">{pr.title}</p>
            <p className="text-[12px] text-secondary m-0 mb-3">
              into {pr.baseRefName} · checks {pr.checks ?? 'none'} ·{' '}
              {pr.unresolvedThreads ? `${pr.unresolvedThreads} open threads` : 'no open threads'}
            </p>
            {pr.state === 'OPEN' ? (
              <button className="ui-btn-accent" onClick={() => void merge()} disabled={!mergeReady || merging}>
                {merging ? 'Merging…' : mergeReady ? 'Merge pull request' : 'Not ready to merge'}
              </button>
            ) : null}
          </>
        ) : (
          <p className="text-[13px] text-secondary m-0">No pull request yet.</p>
        )}
      </section>

      <section className="ui-card p-4">
        <p className="text-[13px] text-primary font-semibold m-0 mb-2">
          Files changed{review ? ` (${review.changes.length}) vs ${review.base}` : ''}
        </p>
        {review?.changes.map((change) => (
          <p key={change.path} className="font-geist-mono text-[12px] m-0 py-0.5 break-all sm:truncate">
            <span className={FILE_STATUS[change.status?.[0] ?? 'M'] ?? 'text-amber-300'}>{change.status?.[0]}</span>{' '}
            <span className="text-primary">{change.path}</span>
          </p>
        ))}
        {review?.changes.length === 0 ? <p className="text-[12.5px] text-muted m-0">No committed changes.</p> : null}
      </section>

      <section className="ui-card p-4">
        <p className="text-[13px] text-primary font-semibold m-0 mb-2">
          Commits{review ? ` (${review.commits.length})` : ''}
        </p>
        {review?.commits.map((commit) => (
          <div key={commit.hash} className="py-1">
            <p className="text-[12.5px] text-primary m-0 truncate">{commit.subject}</p>
            <p className="font-geist-mono text-[11px] text-muted m-0">
              {commit.shortHash} · {commit.author} · {timeAgo(commit.ts)}
            </p>
          </div>
        ))}
      </section>
    </div>
  );
}

const CHECK_GLYPH: Record<string, { glyph: string; cls: string }> = {
  pass: { glyph: '✓', cls: 'text-emerald-300' },
  fail: { glyph: '✕', cls: 'text-red-300' },
  pending: { glyph: '●', cls: 'text-amber-300' },
  skipping: { glyph: '–', cls: 'text-muted' },
  cancel: { glyph: '⊘', cls: 'text-muted' },
};

function ChecksPanel({ worktreeId, sidecarId }: { worktreeId?: string; sidecarId?: string }) {
  const remote = useRemote();
  const [detail, setDetail] = useState<PrDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!worktreeId || !sidecarId) {
      return;
    }

    remote
      .rpcFor<PrDetail>(sidecarId, 'pr.detail', { worktreeId })
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load checks'));
  }, [worktreeId, sidecarId]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4 flex flex-col gap-4">
      {error ? <p className="text-[13px] text-secondary">{error}</p> : null}
      <section className="ui-card p-4">
        <p className="text-[13px] text-primary font-semibold m-0 mb-2">
          Checks{detail ? ` (${detail.checks.length})` : ''}
        </p>
        {detail?.checks.map((check) => {
          const meta = CHECK_GLYPH[check.bucket] ?? CHECK_GLYPH.pending;

          return (
            <p key={`${check.workflow ?? ''}/${check.name}`} className="text-[12.5px] m-0 py-0.5 truncate">
              <span className={meta.cls}>{meta.glyph}</span>{' '}
              {check.link ? (
                <a
                  href={check.link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary no-underline hover:underline"
                >
                  {check.workflow ? `${check.workflow} / ` : ''}
                  {check.name}
                </a>
              ) : (
                <span className="text-primary">
                  {check.workflow ? `${check.workflow} / ` : ''}
                  {check.name}
                </span>
              )}
            </p>
          );
        })}
        {detail?.checks.length === 0 ? <p className="text-[12.5px] text-muted m-0">No checks reported.</p> : null}
      </section>
      <section className="ui-card p-4">
        <p className="text-[13px] text-primary font-semibold m-0 mb-2">
          Review comments{detail ? ` (${detail.comments.length})` : ''}
        </p>
        {detail?.comments.slice(0, 30).map((comment, i) => (
          <div key={i} className="py-1.5 border-b border-hairline last:border-b-0">
            <p className="text-[11.5px] text-secondary font-semibold m-0">
              {comment.author}
              {comment.path ? ` · ${comment.path}${comment.line ? `:${comment.line}` : ''}` : ''}
            </p>
            <p className="text-[12.5px] text-primary m-0 mt-0.5 line-clamp-5">{comment.body}</p>
          </div>
        ))}
        {detail?.comments.length === 0 ? <p className="text-[12.5px] text-muted m-0">No comments.</p> : null}
      </section>
    </div>
  );
}
